import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import AskepMppSvc from "@/services/AskepMpp";
import { SkriningResponseItem } from "@/types/mpp";

export interface MppState {
  skrining: any[];
  perencanaan: any[];
  implementasi: any[];
  loading: boolean;
  skriningOptions: SkriningResponseItem[];
}

@Module({
  dynamic: true,
  store,
  name: "mpp",
  namespaced: true
})
class MppModule extends VuexModule implements MppState {
  // --- STATE ---
  skrining: any[] = [];
  perencanaan: any[] = [];
  implementasi: any[] = [];
  loading = false;
  skriningOptions: SkriningResponseItem[] = [];

  get getSkrining() {
    return this.skrining.sort((a, b) => {
      const timeA = new Date(a.authored_at || a.created_at || 0).getTime();
      const timeB = new Date(b.authored_at || b.created_at || 0).getTime();
      return timeB - timeA;
    });
  }

  get getPerencanaan() {
    return this.perencanaan.sort((a, b) => {
      const timeA = new Date(a.authored_at || a.created_at || 0).getTime();
      const timeB = new Date(b.authored_at || b.created_at || 0).getTime();
      return timeB - timeA;
    });
  }

  get getImplementasi() {
    return this.implementasi.sort((a, b) => {
      const timeA = new Date(a.authored_at || a.created_at || 0).getTime();
      const timeB = new Date(b.authored_at || b.created_at || 0).getTime();
      return timeB - timeA;
    });
  }

  get getSkriningOption() {
    return this.skriningOptions || [];
  }

  @Mutation
  SET_SKRINING(data: any[]) {
    this.skrining = data;
  }

  @Mutation
  SET_PERENCANAAN(data: any[]) {
    this.perencanaan = data;
  }

  @Mutation
  SET_IMPLEMENTASI(data: any[]) {
    this.implementasi = data;
  }

  @Mutation
  SET_LOADING(status: boolean) {
    this.loading = status;
  }

  @Mutation
  UPSERT_ITEM(payload: { category: string; item: any }) {
    const { category, item } = payload;
    let list: any[] = [];

    if (category === "skrining") list = this.skrining;
    else if (category === "rencana") list = this.perencanaan;
    else if (category === "implementasi") list = this.implementasi;

    const index = list.findIndex((i: any) => i.id === item.id);

    if (index !== -1) {
      list.splice(index, 1, item);
    } else {
      // Add: Masukkan ke awal array (unshift)
      list.unshift(item);
    }
  }

  @Mutation
  REMOVE_ITEM(payload: { category: string; id: number }) {
    const { category, id } = payload;
    if (category === "skrining") {
      this.skrining = this.skrining.filter((i: any) => i.id !== id);
    } else if (category === "rencana") {
      this.perencanaan = this.perencanaan.filter((i: any) => i.id !== id);
    } else if (category === "implementasi") {
      this.implementasi = this.implementasi.filter((i: any) => i.id !== id);
    }
  }

  @Mutation
  SET_SKRINING_OPTIONS(options: SkriningResponseItem[]) {
    this.skriningOptions = options;
  }

  @Action
  async fetchAllMpp(registerId: string | number) {
    if (!registerId) return;
    this.SET_LOADING(true);
    try {
      // Fetch paralel agar lebih cepat
      const [resSkrining, resPerencanaan, resImplementasi] = await Promise.all([
        AskepMppSvc.getByRegister(registerId, "skrining"),
        AskepMppSvc.getByRegister(registerId, "rencana"),
        AskepMppSvc.getByRegister(registerId, "implementasi")
      ]);

      this.SET_SKRINING(resSkrining.data || []);
      this.SET_PERENCANAAN(resPerencanaan.data || []);
      this.SET_IMPLEMENTASI(resImplementasi.data || []);
    } catch (error) {
      console.error("Error fetching all MPP data:", error);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action
  async saveMpp(payload: { category: string; data: any; isEdit: boolean }) {
    const { category, data, isEdit } = payload;

    try {
      const apiData = { ...data };

      // Hapus object unit dan user (karena API hanya butuh ID)
      delete apiData.unit;
      delete apiData.user;

      let resultItem;

      if (isEdit) {
        const res = await AskepMppSvc.update(data.id, apiData, category);

        // MERGE: data (lengkap dengan nama) + res (timestamp, dll)
        resultItem = { ...data, ...res, id: data.id };
      } else {
        // Kirim apiData (yang bersih) ke service
        const apiResponse = await AskepMppSvc.create(apiData, category);

        // MERGE: data (lengkap dengan nama) + apiResponse (id baru, timestamp)
        resultItem = {
          ...data, // Ambil semua data form (termasuk nama unit & user)
          id: apiResponse.id, // Ambil ID baru
          created_at: apiResponse.created_at || new Date().toISOString(),
          updated_at: apiResponse.updated_at || new Date().toISOString()
        };
      }

      // 2. Update State Lokal dengan Data Lengkap (Berisi Nama)
      this.UPSERT_ITEM({ category, item: resultItem });

      return true;
    } catch (error) {
      console.error(`Error saving ${category}:`, error);
      throw error;
    }
  }

  @Action
  async deleteMpp(payload: { category: string; id: number }) {
    const { category, id } = payload;

    try {
      await AskepMppSvc.destroy(id, category, "");

      // Hapus dari State Lokal
      this.REMOVE_ITEM({ category, id });

      return true;
    } catch (error) {
      console.error(`Error deleting ${category}:`, error);
      throw error;
    }
  }

  // MppStore.ts

  @Action
  async fetchSkriningOptions() {
    try {
      const data = await AskepMppSvc.getDataSkrining();

      this.SET_SKRINING_OPTIONS(data);
    } catch (error) {
      console.error("Gagal fetch di Store:", error);
    }
  }
}

export default getModule(MppModule);
